/**
 * calciumScoreCore.js — ядро кальциевого индекса Агатстона.
 *
 * [12.0.52] Шапка переписана: прежняя («УЛЬТИМАТИВНОЕ ядро… QWEN 3.8
 * PATCHED») обещала воркер, перцентили MESA и классификацию бляшек. Воркер
 * не звал никто, перцентили были без источника, классификация — выдумана;
 * всё три убраны (см. надгробия ниже). Здесь остаётся:
 *  - счёт Агатстона по очагам (3D-связность, вес по пику на срезе, нормировка
 *    на толщину 3 мм);
 *  - объём и «масса» кальция;
 *  - ограничение областью сердца и отсев костей по размеру куска — их
 *    передаёт ai/aiModule.calciumScoring (область — core/thoraxCore.js);
 *  - экспорт CSV / JSON / черновик SR.
 * Проверки: tests/calcium_live_test.mjs, tests/thorax_test.mjs.
 */

import { сегментироватьЛёгкие, областьСердца } from '../core/thoraxCore.js';

// ══════════════════════════════════════════════════════════════
//  [12.0.52] УДАЛЕНО: таблица «MESA percentiles» и agatstonPercentile()
// ══════════════════════════════════════════════════════════════
// Таблица перцентилей по возрасту и полу стояла без источника, и её числа
// не совпадают с калькулятором MESA (McClelland RL et al., Circulation 2006 —
// там перцентиль считается по формуле с поправкой на возраст, пол и расу, а
// не по пяти ступенькам). Функцию не звал никто. А поле percentileEstimate
// в результате ставило «перцентиль» по одному баллу, без возраста и пола
// вовсе. Перцентиль без пациента — не перцентиль; убрано.

// ══════════════════════════════════════════════════════════════
//  YIELD TO MAIN THREAD (уступка потока)
// ══════════════════════════════════════════════════════════════

const _yieldToMain = () => new Promise(resolve => {
  if (typeof requestAnimationFrame !== 'undefined') {
    requestAnimationFrame(resolve);
  } else {
    setTimeout(resolve, 0);
  }
});

// ══════════════════════════════════════════════════════════════
//  AGATSTON WEIGHT
// ══════════════════════════════════════════════════════════════

export function agatstonWeight(maxHU) {
  if (maxHU < 130) return 0;
  if (maxHU < 200) return 1;
  if (maxHU < 300) return 2;
  if (maxHU < 400) return 3;
  return 4;
}

// ══════════════════════════════════════════════════════════════
//  3D CONNECTED COMPONENTS (6-связность — Agatston standard)
// ══════════════════════════════════════════════════════════════

/**
 * Плотная разметка связных кусков (массив меток на ВЕСЬ том).
 * [12.0.52] Сам счёт кальция ею больше не пользуется: на томе 512×512×800
 * две её таблицы — 1,7 ГБ. Оставлена для внешних вызовов; счёт идёт
 * разреженно (_кускиРазреженно ниже) — память по числу кандидатов.
 */
export function connectedComponents3D(mask, w, h, d) {
  const totalVoxels = w * h * d;
  const labels = new Int32Array(totalVoxels);
  let queue = null;
  let nextLabel = 1;
  const idx = (x, y, z) => z * w * h + y * w + x;

  for (let z = 0; z < d; z++) {
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const i = idx(x, y, z);
        if (!mask[i] || labels[i]) continue;

        if (!queue) queue = new Int32Array(totalVoxels);

        labels[i] = nextLabel;
        let head = 0, tail = 0;
        queue[tail++] = i;

        while (head < tail) {
          const ci = queue[head++];
          const cz = Math.floor(ci / (w * h));
          const cy = Math.floor((ci % (w * h)) / w);
          const cx = ci % w;

          const nb = [
            cx > 0 ? idx(cx - 1, cy, cz) : -1,
            cx < w - 1 ? idx(cx + 1, cy, cz) : -1,
            cy > 0 ? idx(cx, cy - 1, cz) : -1,
            cy < h - 1 ? idx(cx, cy + 1, cz) : -1,
            cz > 0 ? idx(cx, cy, cz - 1) : -1,
            cz < d - 1 ? idx(cx, cy, cz + 1) : -1,
          ];

          for (const ni of nb) {
            if (ni >= 0 && mask[ni] && !labels[ni]) {
              labels[ni] = nextLabel;
              queue[tail++] = ni;
            }
          }
        }
        nextLabel++;
      }
    }
  }
  return { labels, count: nextLabel - 1 };
}

/**
 * Разреженная разметка: кандидаты — отсортированный список индексов.
 * @returns {{метки: Int32Array, число: number}} метка на каждого кандидата (1…)
 */
function _кускиРазреженно(кандидаты, w, h, d) {
  const n = кандидаты.length;
  const поз = new Map();
  for (let k = 0; k < n; k++) поз.set(кандидаты[k], k);
  const метки = new Int32Array(n);
  const очередь = new Int32Array(n);
  const wh = w * h;
  let число = 0;
  for (let k0 = 0; k0 < n; k0++) {
    if (метки[k0]) continue;
    число++;
    let голова = 0, хвост = 0;
    метки[k0] = число; очередь[хвост++] = k0;
    while (голова < хвост) {
      const k = очередь[голова++];
      const i = кандидаты[k];
      const x = i % w, t = (i - x) / w, y = t % h, z = (t - y) / h;
      const сосед = (j) => { const p = поз.get(j); if (p !== undefined && !метки[p]) { метки[p] = число; очередь[хвост++] = p; } };
      if (x > 0) сосед(i - 1); if (x < w - 1) сосед(i + 1);
      if (y > 0) сосед(i - w); if (y < h - 1) сосед(i + w);
      if (z > 0) сосед(i - wh); if (z < d - 1) сосед(i + wh);
    }
  }
  return { метки, число };
}

/**
 * Велик ли кусок ≥ порога ВО ВСЁМ ТОМЕ (без ограничения областью), к
 * которому принадлежат данные воксели. Кости — позвонки, грудина, рёбра —
 * это куски в десятки миллилитров; коронарный кальцинат — доли миллилитра.
 *
 * Связность — С ПЕРЕШАГИВАНИЕМ до 2 вокселей: при остеопорозе губчатая
 * кость позвонка ниже 130 HU, и выше порога остаётся только тонкий кортекс,
 * разорванный шумом на дуги. Каждая дуга мала и прошла бы за кальцинат
 * (замер на синтетике: губчатая 90 HU — 64 «очага», 26 175 вместо 775).
 * С перешагиванием дуги одного позвонка и соседние позвонки через диск
 * собираются в одну кость. Коронарная артерия от кости дальше 2 вокселей
 * (между ними перикард и жир), поэтому к кости не прилипает.
 *
 * Обход останавливается, как только набран предел: кость целиком не нужна.
 */
function _крупныйКусок(pixels, w, h, d, семена, порог, пределВокс, кость) {
  const wh = w * h;
  const видно = new Set();
  const стек = [];
  for (const i of семена) { if (кость.has(i)) return true; видно.add(i); стек.push(i); }
  while (стек.length) {
    const i = стек.pop();
    if (кость.has(i)) { for (const j of видно) кость.add(j); return true; }
    const x = i % w, t = (i - x) / w, y = t % h, z = (t - y) / h;
    for (let dz = -2; dz <= 2; dz++) {
      const zz = z + dz; if (zz < 0 || zz >= d) continue;
      for (let dy = -2; dy <= 2; dy++) {
        const yy = y + dy; if (yy < 0 || yy >= h) continue;
        for (let dx = -2; dx <= 2; dx++) {
          const xx = x + dx; if (xx < 0 || xx >= w) continue;
          const j = zz * wh + yy * w + xx;
          if (!видно.has(j) && pixels[j] >= порог) { видно.add(j); стек.push(j); }
        }
      }
    }
    if (видно.size > пределВокс) { for (const j of видно) кость.add(j); return true; }
  }
  return false;
}

// ══════════════════════════════════════════════════════════════
//  [12.0.52] УДАЛЕНО: classifyPlaque() — «мягкая уязвимая бляшка»
// ══════════════════════════════════════════════════════════════
// Функция делила кальцинаты по плотности на «Кальцинированная / Смешанная /
// Мягкая (некальцинированная) — потенциально уязвимая — высокий риск». Всё,
// что выше 130 HU, — кальцинат по определению Агатстона; «мягкую» бляшку по
// бесконтрастной КТ не увидеть вообще, а ярлык «уязвимая — высокий риск»
// ставился любому кальцинату 130–200 HU. Источника у правил не было. Во
// вкладке ИИ по нему печаталось «Уязвимых бляшек: N» (поле isVulnerable,
// которого не существовало, — всегда 0). Убрано вместе с полем.

// ══════════════════════════════════════════════════════════════
//  CALCIUM SCORE (основная функция, асинхронная с yield)
// ══════════════════════════════════════════════════════════════

/**
 * Кальциевый индекс Агатстона по очагам (Agatston AS et al., JACC 1990):
 * порог 130 HU, вес среза очага по его пиковому HU (1: 130–199, 2: 200–299,
 * 3: 300–399, 4: ≥400), площадь × вес, сумма по срезам и очагам. Верхней
 * границы нет — плотный кальцинат бывает и выше 1000 HU.
 *
 * Опции [12.0.52]:
 *   область — {от, до: Int16Array(depth*height) — границы по x на строке
 *             (z*height + y), разрешено: Uint8Array, разрешёнКод: number}.
 *             Кандидат берётся только внутри области (thoraxCore.областьСердца).
 *   крупнееМм3 — очаг, чей связный кусок ≥ порога во всём томе больше этого
 *             объёма, — кость (позвонок, грудина, ребро), а не кальцинат.
 *   cardiacROI — прежняя прямоугольная рамка в долях кадра. Оставлена для
 *             проверок на простых фантомах; живой путь её не включает.
 */
export async function calciumScore(pixels, width, height, depth, spacing, options = {}) {
  const {
    huThreshold = 130,
    // [12.0.52] Было huMax = 1000: воксели плотнее 1000 HU выпадали, и плотный
    // кальцинат распадался на куски или пропадал. У Агатстона верхней границы
    // нет. Кости отделяются не плотностью (губчатая кость — 150…300 HU), а
    // размером куска и областью сердца.
    huMax = Infinity,
    minVoxels = 3,
    cardiacROI = false,
    область = null,
    крупнееМм3 = Infinity,
    onProgress,
    cachedMask,
    chunkSize = 50,
    returnMask = false,
  } = options;

  const [dx, dy] = spacing;
  const sliceThicknessMm = spacing?.[2] > 0 ? spacing[2] : 3.0;
  const thicknessNorm = sliceThicknessMm / 3.0;
  const voxelAreaMm2 = dx * dy;
  const voxelVolumeMm3 = dx * dy * (spacing[2] || 3.0);

  const getHU = (i) => {
    const v = pixels[i];
    return Number.isFinite(v) ? v : 0;
  };

  let roiMinZ = 0, roiMaxZ = depth - 1;
  let roiMinX = 0, roiMaxX = width - 1;
  let roiMinY = 0, roiMaxY = height - 1;

  if (cardiacROI) {
    const rz = cardiacROI.z || [0.30, 0.75];
    const rx = cardiacROI.x || [0.25, 0.75];
    const ry = cardiacROI.y || [0.20, 0.80];
    roiMinZ = Math.floor(depth * rz[0]);
    roiMaxZ = Math.floor(depth * rz[1]);
    roiMinX = Math.floor(width * rx[0]);
    roiMaxX = Math.floor(width * rx[1]);
    roiMinY = Math.floor(height * ry[0]);
    roiMaxY = Math.floor(height * ry[1]);
  }

  const sliceSize = width * height;
  const кандидатыСписок = [];
  const mask = returnMask && !cachedMask ? new Uint8Array(width * height * depth) : null;

  onProgress?.('mask', 0);
  for (let cz = 0; cz < depth; cz += chunkSize) {
    const chunkEnd = Math.min(cz + chunkSize, depth);
    for (let z = Math.max(cz, roiMinZ); z <= Math.min(chunkEnd - 1, roiMaxZ); z++) {
      const zOff = z * sliceSize;
      for (let y = roiMinY; y <= roiMaxY; y++) {
        let xa = roiMinX, xb = roiMaxX;
        if (область) {
          const r = z * height + y;
          xa = Math.max(xa, область.от[r]); xb = Math.min(xb, область.до[r]);
          if (xa > xb) continue;
        }
        const yOff = zOff + y * width;
        for (let x = xa; x <= xb; x++) {
          const i = yOff + x;
          if (cachedMask) { if (cachedMask[i] && getHU(i) >= huThreshold) кандидатыСписок.push(i); continue; }
          const hu = getHU(i);
          if (hu < huThreshold || hu > huMax) continue;
          if (область?.разрешено && область.разрешено[i] !== область.разрешёнКод) continue;
          кандидатыСписок.push(i);
          if (mask) mask[i] = 1;
        }
      }
    }
    onProgress?.('mask', Math.round((chunkEnd / depth) * 30));
    await _yieldToMain(); // 🔴 Уступка потока для UI
  }

  onProgress?.('components', 30);
  const кандидаты = Int32Array.from(кандидатыСписок);
  const { метки, число: count } = _кускиРазреженно(кандидаты, width, height, depth);

  if (count === 0) {
    onProgress?.('done', 100);
    return {
      totalScore: 0, volumeScore: 0, massScore: 0,
      lesionCount: 0, lesions: [], perSlice: new Float32Array(depth),
      riskCategory: 'Нет кальциноза', исключеноКрупных: 0,
      mask: returnMask ? (mask || cachedMask) : undefined,
      threshold: huThreshold, huMax, spacing, dimensions: [width, height, depth],
    };
  }

  onProgress?.('stats', 60);

  const compStats = new Array(count + 1);
  for (let i = 1; i <= count; i++) {
    compStats[i] = {
      voxelCount: 0, maxHU: -Infinity, sumHU: 0, sumX: 0, sumY: 0, sumZ: 0,
      zStatsVoxels: new Map(), zStatsMaxHU: new Map(), индексы: [],
      bbox: { minX: width, maxX: 0, minY: height, maxY: 0, minZ: depth, maxZ: 0 },
    };
  }

  for (let k = 0; k < кандидаты.length; k++) {
    const lbl = метки[k];
    const i = кандидаты[k];
    const hu = getHU(i);
    const z = Math.floor(i / sliceSize);
    const y = Math.floor((i % sliceSize) / width);
    const x = i % width;

    const st = compStats[lbl];
    st.voxelCount++;
    st.sumHU += hu;
    st.sumX += x;
    st.sumY += y;
    st.sumZ += z;
    st.индексы.push(i);
    if (hu > st.maxHU) st.maxHU = hu;
    if (x < st.bbox.minX) st.bbox.minX = x;
    if (x > st.bbox.maxX) st.bbox.maxX = x;
    if (y < st.bbox.minY) st.bbox.minY = y;
    if (y > st.bbox.maxY) st.bbox.maxY = y;
    if (z < st.bbox.minZ) st.bbox.minZ = z;
    if (z > st.bbox.maxZ) st.bbox.maxZ = z;

    st.zStatsVoxels.set(z, (st.zStatsVoxels.get(z) || 0) + 1);
    if (!(st.zStatsMaxHU.get(z) >= hu)) st.zStatsMaxHU.set(z, hu);
  }

  onProgress?.('scoring', 80);

  let totalScore = 0, totalVolumeScore = 0, totalMassScore = 0, lesionCount = 0;
  let исключеноКрупных = 0;
  const lesions = [];
  const perSlice = new Float32Array(depth);
  const пределВокс = Number.isFinite(крупнееМм3) ? Math.ceil(крупнееМм3 / voxelVolumeMm3) : Infinity;
  const кость = new Set();

  for (let i = 1; i <= count; i++) {
    const st = compStats[i];
    if (st.voxelCount < minVoxels) continue;
    if (Number.isFinite(пределВокс)
      && (st.voxelCount > пределВокс || _крупныйКусок(pixels, width, height, depth, st.индексы, huThreshold, пределВокс, кость))) {
      исключеноКрупных++;
      continue;
    }

    lesionCount++;
    let lesionScore = 0;

    for (const [z, voxels] of st.zStatsVoxels) {
      if (voxels >= minVoxels) {
        const s = voxels * voxelAreaMm2 * agatstonWeight(st.zStatsMaxHU.get(z)) * thicknessNorm;
        lesionScore += s;
        perSlice[z] += s;
      }
    }

    totalScore += lesionScore;
    const volumeMm3 = st.voxelCount * voxelVolumeMm3;
    totalVolumeScore += volumeMm3;
    const mass = (st.sumHU / st.voxelCount) * volumeMm3 * 0.001;
    totalMassScore += mass;

    lesions.push({
      id: i,
      voxels: st.voxelCount,
      maxHU: st.maxHU,
      meanHU: Math.round(st.sumHU / st.voxelCount),
      volumeMm3: Math.round(volumeMm3 * 10) / 10,
      massScore: Math.round(mass * 10) / 10,
      // [12.0.52] Счёт очага — без округления: сумма по отмеченным очагам
      // в окне должна совпадать с итогом, а округлённые слагаемые расходятся.
      scoreExact: lesionScore,
      score: Math.round(lesionScore),
      centroid: {
        x: Math.round(st.sumX / st.voxelCount),
        y: Math.round(st.sumY / st.voxelCount),
        z: Math.round(st.sumZ / st.voxelCount),
      },
      bbox: {
        minX: st.bbox.minX, maxX: st.bbox.maxX,
        minY: st.bbox.minY, maxY: st.bbox.maxY,
        minZ: st.bbox.minZ, maxZ: st.bbox.maxZ,
      },
    });
  }

  lesions.sort((a, b) => b.scoreExact - a.scoreExact);

  const score = Math.round(totalScore);
  onProgress?.('done', 100);

  return {
    totalScore: score,
    volumeScore: Math.round(totalVolumeScore * 10) / 10,
    massScore: Math.round(totalMassScore * 10) / 10,
    lesionCount,
    lesions,
    perSlice,
    riskCategory: категорияАгатстона(score),
    исключеноКрупных,
    mask: returnMask ? (mask || cachedMask) : undefined, // 🔴 Возвращаем маску для кэширования
    threshold: huThreshold,
    huMax,
    spacing,
    dimensions: [width, height, depth],
    timestamp: Date.now(),
  };
}

/**
 * Категория по индексу — ОДНА на программу (раньше их было две разных:
 * здесь «Лёгкий/Умеренный», в aiModule — «Очень низкий/Высокий риск», а в
 * окне подпись «ACC/AHA 2010»). Ступени 0 / 1–10 / 11–100 / 101–400 / >400 —
 * выраженность кальциноза (Rumberger JA et al., Mayo Clin Proc 1999; ПО
 * ПАМЯТИ). Это мера количества кальция, а не риск пациента: риск зависит
 * ещё от возраста, пола и факторов риска.
 */
export function категорияАгатстона(score) {
  if (!(score > 0)) return 'Нет кальциноза';
  if (score <= 10) return 'Минимальный кальциноз';
  if (score <= 100) return 'Лёгкий кальциноз';
  if (score <= 400) return 'Умеренный кальциноз';
  return 'Выраженный кальциноз';
}

/** Уровень 0…4 для цвета — по тем же ступеням. */
export function уровеньАгатстона(score) {
  if (!(score > 0)) return 0;
  if (score <= 10) return 1;
  if (score <= 100) return 2;
  if (score <= 400) return 3;
  return 4;
}


/**
 * Кальциевый индекс по КТ груди — то, что зовут все кнопки (через
 * ai/aiModule.calciumScoring). [12.0.52]
 *
 * Прежде счёт шёл в прямоугольнике посреди кадра: туда попадали позвонки,
 * грудина и рёбра. На синтетической груди с тремя коронарными кальцинатами
 * и одним кальцинатом стенки аорты (эталон 775) выходило 218 425 —
 * «очень высокий риск». Теперь:
 *  1. лёгкие и тело ищутся по снимку (core/thoraxCore.js); нет лёгких —
 *     отказ словами;
 *  2. контраст в полостях сердца — отказ: индекс определён только для
 *     бесконтрастной КТ (Agatston 1990), контрастная кровь сама выше 130 HU;
 *  3. кальций ищется в области между лёгкими (сердце и средостение);
 *  4. куски крупнее 5 мл — кости, а не кальцинаты (ПО ПАМЯТИ: позвонок,
 *     грудина, ребро — десятки мл; коронарный очаг — доли мл).
 * В область попадают и НЕкоронарные кальцинаты (клапан, кольцо митрального
 * клапана, стенка аорты, хрящи у грудины): их снимает врач в окне
 * результата, поэтому очаги отдаются списком с центрами.
 */
export async function кальцийГруди(pixels, width, height, depth, spacing, options = {}) {
  const dims = [width, height, depth];
  const лёгкие = сегментироватьЛёгкие(pixels, dims, spacing, { direction: options.direction });
  if (!лёгкие.ok) return { ok: false, отказ: `не найдена грудная клетка: ${лёгкие.причина}` };
  const область = областьСердца(pixels, dims, spacing, лёгкие, { direction: options.direction });
  if (!область.ok) return { ok: false, отказ: `не найдена область сердца: ${область.причина}` };
  if (область.контраст) {
    return {
      ok: false,
      отказ: `в полостях сердца контраст (медиана мягких тканей ${область.медианаHU} HU). `
        + 'Индекс Агатстона считается только по бесконтрастной КТ: контрастная кровь сама плотнее 130 HU и была бы сосчитана как кальций.',
      медианаHU: область.медианаHU,
    };
  }
  const r = await calciumScore(pixels, width, height, depth, spacing, { область, крупнееМм3: КОСТЬ_ММ3 });
  return {
    ok: true,
    totalScore: r.totalScore || 0,
    volumeMm3: r.volumeScore ?? 0,
    massScore: r.massScore ?? 0,
    lesionCount: r.lesionCount ?? 0,
    risk: категорияАгатстона(r.totalScore || 0),
    riskLevel: уровеньАгатстона(r.totalScore || 0),
    lesions: r.lesions || [],
    perSlice: r.perSlice,
    исключеноКрупных: r.исключеноКрупных || 0,
    медианаHU: область.медианаHU,
    толщинаМм: spacing?.[2] || null,
  };
}

/** Кусок ≥130 HU крупнее этого — кость (мм³). ПО ПАМЯТИ, см. кальцийГруди. */
export const КОСТЬ_ММ3 = 5000;

// ══════════════════════════════════════════════════════════════
//  MULTI-THRESHOLD ANALYSIS (FIXED: использует кэш маски)
// ══════════════════════════════════════════════════════════════

export async function multiThresholdScore(pixels, width, height, depth, spacing, options = {}) {
  const thresholds = options.thresholds || [130, 200, 300];
  const results = {};
  let cachedMask = null;

  for (const threshold of thresholds) {
    const opts = { 
      ...options, 
      huThreshold: threshold, 
      cachedMask,
      returnMask: !cachedMask, // 🔴 Запрашиваем маску только на первом проходе
    };
    results[threshold] = await calciumScore(pixels, width, height, depth, spacing, opts);
    if (!cachedMask && results[threshold].mask) {
      cachedMask = results[threshold].mask;
    }
  }

  return results;
}

// ══════════════════════════════════════════════════════════════
//  ЭКСПОРТ
// ══════════════════════════════════════════════════════════════

export function exportToCSV(result) {
  // [12.0.52] Столбец «Type» (классификация бляшки) убран вместе с classifyPlaque.
  const headers = ['ID', 'HU max', 'HU mean', 'Volume mm³', 'Score', 'Centroid X', 'Centroid Y', 'Centroid Z'];
  const rows = result.lesions.map(l => [
    l.id, l.maxHU, l.meanHU, l.volumeMm3, l.score,
    l.centroid.x, l.centroid.y, l.centroid.z,
  ]);
  return [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
}

export function exportToJSON(result) {
  return JSON.stringify({
    version: 1,
    exportDate: new Date().toISOString(),
    viewer: 'Mikhmel Cornerstone4D v6',
    ...result,
  }, null, 2);
}

export function exportToDICOMSR(result, studyUID, patientName) {
  // 🔴 FIX: ContentSequence — массив, RelationshipType указан
  const findings = result.lesions.map((l, i) => ({
    RelationshipType: 'CONTAINS',
    ConceptNameCodeSequence: { 
      CodeValue: '112031', 
      CodingSchemeDesignator: 'DCM', 
      CodeMeaning: 'Calcium Score' 
    },
    MeasuredValueSequence: { 
      NumericValue: l.score, 
      MeasurementUnitsCodeSequence: { 
        CodeValue: '1', 
        CodingSchemeDesignator: 'DCM', 
        CodeMeaning: 'Agatston Units' 
      } 
    },
    // [12.0.52] Вложенная «классификация бляшки» убрана вместе с classifyPlaque.
  }));

  return JSON.stringify({
    Modality: 'SR',
    SOPClassUID: '1.2.840.10008.5.1.4.1.1.88.33',
    StudyInstanceUID: studyUID || '',
    PatientName: patientName || '',
    ContentSequence: [
      { 
        RelationshipType: 'CONTAINS',
        ConceptNameCodeSequence: { 
          CodeValue: 'TOTAL_AGATSTON', 
          CodingSchemeDesignator: '99MIKHMEL', 
          CodeMeaning: 'Total Agatston Score' 
        }, 
        MeasuredValueSequence: { 
          NumericValue: result.totalScore, 
          MeasurementUnitsCodeSequence: { 
            CodeValue: '1', 
            CodingSchemeDesignator: 'DCM', 
            CodeMeaning: 'Agatston Units' 
          } 
        } 
      },
      { 
        RelationshipType: 'CONTAINS',
        ConceptNameCodeSequence: { 
          CodeValue: 'LESION_COUNT', 
          CodingSchemeDesignator: '99MIKHMEL', 
          CodeMeaning: 'Lesion Count' 
        }, 
        MeasuredValueSequence: { 
          NumericValue: result.lesionCount, 
          MeasurementUnitsCodeSequence: { 
            CodeValue: '{lesions}', 
            CodingSchemeDesignator: 'UCUM', 
            CodeMeaning: 'lesions' 
          } 
        } 
      },
      ...findings,
    ],
    _exportDate: new Date().toISOString(),
    _viewer: 'Mikhmel Cornerstone4D v6',
  }, null, 2);
}

// ══════════════════════════════════════════════════════════════
//  [12.0.52] УДАЛЕНО: createCalciumWorker()
// ══════════════════════════════════════════════════════════════
// Собирал воркер из текста функций (toString). Не звал никто; после переделки
// счёт зовёт вспомогательные функции модуля, и склеенный текстом воркер падал
// бы на первом же вызове. Мёртвый и ломкий — убран.

export default {
  agatstonWeight, connectedComponents3D, calciumScore, кальцийГруди, категорияАгатстона, уровеньАгатстона,
  multiThresholdScore, exportToCSV, exportToJSON, exportToDICOMSR,
};